import { Box, HStack, Text, VStack } from '@chakra-ui/react'
import { Value } from '@helo/utils'
import { useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { withUnicodeRouteArrow } from '../../../utils/routeLabelDisplay'
import { ChartMarker } from '../common/ChartMarker'
import { formatCategoryTooltipLabel } from '../common/chartCategoryDate'
import {
  ChartValueFormat,
  formatChartDataLabelValue,
} from '../common/chartFormat'
import {
  FLOATING_CHART_TOOLTIP_CURSOR_OFFSET_PX,
  useChartTooltipPointer,
} from './ChartTooltipPointerContext'
import { FloatingTooltipKvRow } from './FloatingTooltipKvRow'

export type FloatingCartesianSeriesMeta = {
  dataKey: string
  name: string
  color: string
}

export type FloatingCartesianExtraRow = {
  dataKey: string
  label: string
}

/** `line`: shared tooltip, dot per series row, category header only. `bar`: per-bar/segment hover, bar swatch in header, one value row + extras. */
export type FloatingCartesianTooltipLayout = 'line' | 'bar'

const clampTooltipPosition = (
  left: number,
  top: number,
  width: number,
  height: number,
) => {
  const pad = 8
  const maxL = Math.max(pad, window.innerWidth - width - pad)
  const maxT = Math.max(pad, window.innerHeight - height - pad)
  return {
    left: Math.min(Math.max(pad, left), maxL),
    top: Math.min(Math.max(pad, top), maxT),
  }
}

export type FloatingCartesianChartTooltipProps = {
  active?: boolean
  payload?: any[]
  label?: string | number
  nameKey: string
  series: FloatingCartesianSeriesMeta[]
  extraRows?: FloatingCartesianExtraRow[]
  floatingLayout: FloatingCartesianTooltipLayout
  /** When category values parse as dates, format the header with [date-fns](https://date.fns.org/v3/docs/format). Default `MMM yyyy`. */
  categoryAxisDateFormat?: string
  categoryAxisIsDate?: boolean
  /** Per-chart numeric formatting for metric + extra values (precision, suffix, percent, …). */
  valueFormat?: ChartValueFormat
}

/**
 * Floating cartesian tooltip: `line` = shared multi-series with dot per row;
 * `bar` = one hovered bar/segment with bar swatch in header + value + configured extras (e.g. container count from `tooltipExtraFields` on `ChartWidget`).
 */
export const FloatingCartesianChartTooltip = ({
  active,
  payload,
  label,
  nameKey,
  series,
  extraRows = [],
  floatingLayout,
  categoryAxisDateFormat,
  categoryAxisIsDate = false,
  valueFormat,
}: FloatingCartesianChartTooltipProps) => {
  const pointer = useChartTooltipPointer()
  const panelRef = useRef<HTMLDivElement>(null)
  const [adjusted, setAdjusted] = useState<{
    left: number
    top: number
  } | null>(null)

  const body = useMemo(() => {
    if (!active || !payload?.length) return null

    const rows = payload.filter((p: { type?: string }) => p.type !== 'none')
    if (rows.length === 0) return null

    const rowData = rows[0]?.payload as Record<string, unknown> | undefined
    const rawCategory = String(rowData?.[nameKey] ?? label ?? '')
    const categoryLabel = withUnicodeRouteArrow(rawCategory)
    const categoryDisplayLabel = categoryAxisIsDate
      ? formatCategoryTooltipLabel(categoryLabel, categoryAxisDateFormat)
      : categoryLabel

    const seriesLabelByKey = new Map(
      series.map(s => [s.dataKey, s.name] as const),
    )
    const seriesColorByKey = new Map(
      series.map(s => [s.dataKey, s.color] as const),
    )

    const metricBlocks = rows.map((p: any) => {
      const dk = p.dataKey as string
      const seriesName = seriesLabelByKey.get(dk) ?? p.name ?? p.dataKey ?? dk
      const color =
        (typeof p.color === 'string' && p.color) ||
        (typeof p.fill === 'string' && p.fill) ||
        seriesColorByKey.get(dk) ||
        '#fff'
      return {
        key: dk,
        label: String(seriesName),
        value: formatChartDataLabelValue(p.value, valueFormat),
        color,
      }
    })

    const headerBarColor =
      metricBlocks[0]?.color ||
      (typeof rows[0]?.color === 'string' && rows[0].color) ||
      (typeof rows[0]?.fill === 'string' && rows[0].fill) ||
      series[0]?.color ||
      '#fff'

    const extras = extraRows
      .map(er => {
        const raw = rowData?.[er.dataKey]
        if (raw === undefined || raw === null) return null
        return {
          key: er.dataKey,
          label: er.label,
          value: formatChartDataLabelValue(raw, valueFormat),
        }
      })
      .filter(Value.isNotNullable)

    return {
      categoryLabel: categoryDisplayLabel,
      headerBarColor,
      metricBlocks,
      extras,
    }
  }, [
    active,
    categoryAxisDateFormat,
    categoryAxisIsDate,
    extraRows,
    label,
    nameKey,
    payload,
    series,
    valueFormat,
  ])

  const draftLeft = pointer
    ? pointer.clientX + FLOATING_CHART_TOOLTIP_CURSOR_OFFSET_PX
    : 0
  const draftTop = pointer
    ? pointer.clientY + FLOATING_CHART_TOOLTIP_CURSOR_OFFSET_PX
    : 0

  useLayoutEffect(() => {
    if (!body || !pointer) {
      setAdjusted(null)
      return
    }
    const el = panelRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    const next = clampTooltipPosition(
      draftLeft,
      draftTop,
      rect.width,
      rect.height,
    )
    setAdjusted(prev =>
      prev?.left === next.left && prev?.top === next.top ? prev : next,
    )
  }, [body, draftLeft, draftTop, pointer])

  if (!body || !pointer) {
    return null
  }

  const { categoryLabel, headerBarColor, metricBlocks, extras } = body
  const left = adjusted?.left ?? draftLeft
  const top = adjusted?.top ?? draftTop

  const header =
    floatingLayout === 'bar' ? (
      <HStack spacing="8px" align="center" w="100%">
        <ChartMarker type="bar" color={headerBarColor} />
        <Text color="white" fontWeight="700" fontSize="13px" noOfLines={3}>
          {categoryLabel}
        </Text>
      </HStack>
    ) : (
      <Text
        color="white"
        fontWeight="700"
        fontSize="13px"
        noOfLines={3}
        w="100%"
      >
        {categoryLabel}
      </Text>
    )

  const metrics =
    floatingLayout === 'line'
      ? metricBlocks.map(m => (
          <HStack key={m.key} spacing="8px" align="center" w="100%" minW={0}>
            <ChartMarker type="line" linePresentation="dot" color={m.color} />
            <Box flex="1" minW={0}>
              <FloatingTooltipKvRow label={m.label} value={m.value} />
            </Box>
          </HStack>
        ))
      : metricBlocks.map(m => (
          <Box key={m.key} w="100%">
            <FloatingTooltipKvRow label={m.label} value={m.value} />
          </Box>
        ))

  const panel = (
    <Box
      ref={panelRef}
      data-testid="floating-chart-tooltip"
      position="fixed"
      left={`${left}px`}
      top={`${top}px`}
      zIndex={10000}
      bg="presets.overlay.turquoise100_95"
      borderRadius={0}
      px="12px"
      py="10px"
      minW="200px"
      maxW="320px"
      boxShadow="0 4px 16px rgba(0,0,0,0.35)"
    >
      <VStack align="stretch" spacing="6px" alignItems="flex-start">
        {header}
        {metrics}
        {extras.map(ex => (
          <Box key={ex.key} w="100%">
            <FloatingTooltipKvRow label={ex.label} value={ex.value} />
          </Box>
        ))}
      </VStack>
    </Box>
  )

  return createPortal(panel, document.body)
}
